import * as app from 'durandal/app';
import * as dialog from 'plugins/dialog';
import * as segmentAnalytics from 'segmentAnalytics';

import SessionTimeoutFragment from '@/legacy/components/user/signin/sessionTimeout/SessionTimeoutFragment';
import SsoSessionTimeoutDialog from '@/legacy/components/user/signin/ssoSessionTimeout/SsoSessionTimeoutDialog';
import BaseModal from '@/legacy/modals/BaseModal';
import RealtimeEvent from '@/legacy/services/realtime/models/realtimeEvent';
import * as userService from '@/legacy/services/user';

import { EventName, FormEventType } from '../features/analytics/helper';

declareModules({
  'modals/sessionTimeout.html': () => import('@/legacy/modals/sessionTimeout.html'),
});

class SessionTimeout extends BaseModal {
  view = 'modals/sessionTimeout';

  public react = {
    fragments: {
      SessionTimeoutFragment,
      SsoSessionTimeoutDialog,
    },
  };

  public email: string;

  public showSsoTimeoutModal = ko.pureComputed({
    read: () => userService.Instance.profile()?.isSingleSignOnSession(),
    deferEvaluation: true,
  });

  /**
   * This field ensures that only one SessionTimeout dialog is rendered, and de-duplicates calls
   * that happen in error handlers and whatnot. It should resolve when the dialog closes.
   */
  private static dialogPromise: Promise<boolean> | null = null;

  constructor(email: string) {
    super();
    this.email = email;
  }

  public onLoginSuccess() {
    dialog.close(this, true);
  }

  public onLogout() {
    segmentAnalytics.track(EventName.UserSignedOut, {
      source: FormEventType.ButtonClicked,
    });

    segmentAnalytics.reset();

    userService.Instance.Logout();
  }

  public static show(email: string): Promise<boolean> {
    if (SessionTimeout.dialogPromise != null) {
      return SessionTimeout.dialogPromise;
    }

    const sessionTimeoutDialog = new SessionTimeout(email);
    SessionTimeout.dialogPromise = Promise.resolve(dialog.show(sessionTimeoutDialog)).then(Boolean);

    // If we lose connection with SignalR while timeout is visible logout the user
    const realtimeListener = app.on(RealtimeEvent.Offline).then(() => {
      dialog.close(sessionTimeoutDialog, undefined); // This will call Logout()
    }, this);

    SessionTimeout.dialogPromise.finally(() => {
      SessionTimeout.dialogPromise = null;
      realtimeListener.off();
    });

    return SessionTimeout.dialogPromise;
  }

  /** Returns `true` if the SessionTimeout dialog is currently displayed. */
  public static get isDisplayed() {
    return this.dialogPromise != null;
  }
}

export default SessionTimeout;
